import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { alsDauer, alsUhrzeit, api } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { Besetzungsdialog } from './Besetzungsdialog'
import { Wochenplan } from './Wochenplan'
import type { Tagesplan, Zeile } from './typen'
import type { Tageslage } from '../verkehrsmeldungen/typen'

const WOCHENTAG: Record<string, string> = {
  MONDAY: 'Montag',
  TUESDAY: 'Dienstag',
  WEDNESDAY: 'Mittwoch',
  THURSDAY: 'Donnerstag',
  FRIDAY: 'Freitag',
  SATURDAY: 'Samstag',
  SUNDAY: 'Sonntag',
}

function heuteAlsIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function verschiebe(datum: string, tage: number): string {
  const d = new Date(datum + 'T12:00:00')
  d.setDate(d.getDate() + tage)
  return d.toISOString().slice(0, 10)
}

export function Disposition() {
  const { hatRolle } = useAuth()
  const darfDisponieren = hatRolle('ADMIN', 'DISPONENT')

  const [datum, setDatum] = useState(heuteAlsIso())
  const [plan, setPlan] = useState<Tagesplan | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [dialog, setDialog] = useState<Zeile | null>(null)
  const [wochenansicht, setWochenansicht] = useState(false)
  const [offen, setOffen] = useState<number | null>(null)
  // Kursnummer → Titel der Verkehrsmeldungen, die an diesem Tag seine Linien treffen
  const [umleitungen, setUmleitungen] = useState<Record<string, string[]>>({})

  const laden = useCallback(async () => {
    setLaedt(true)
    try {
      setPlan(await api.get<Tagesplan>(`/api/disposition/tagesplan?datum=${datum}`))
      setFehler(null)
      // Getrennt geholt und ohne Fehlermeldung: Fehlen die Meldungen, bleibt die
      // Disposition trotzdem benutzbar — nur ohne Zeichen.
      api
        .get<Tageslage>(`/api/verkehrsmeldungen/lage?datum=${datum}`)
        .then((lage) => {
          const jeKurs: Record<string, string[]> = {}
          for (const { meldung, kurse } of lage.aktiv) {
            for (const k of kurse) (jeKurs[k.kursnummer] ??= []).push(meldung.titel)
          }
          setUmleitungen(jeKurs)
        })
        .catch(() => setUmleitungen({}))
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen')
    } finally {
      setLaedt(false)
    }
  }, [datum])

  useEffect(() => {
    void laden()
  }, [laden])

  async function loesen(zeile: Zeile) {
    if (!confirm(`Besetzung von Kurs ${zeile.kursnummer} aufheben?`)) return
    try {
      await api.loeschen(`/api/disposition/besetzung?datum=${datum}&kursId=${zeile.kursId}`)
      await laden()
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Aufheben fehlgeschlagen')
    }
  }

  function schwerste(zeile: Zeile): 'SPERRE' | 'WARNUNG' | null {
    if (zeile.befunde.some((b) => b.schwere === 'SPERRE')) return 'SPERRE'
    if (zeile.befunde.some((b) => b.schwere === 'WARNUNG')) return 'WARNUNG'
    return null
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Disposition</h1>
          <p>Kurse mit Fahrer und Fahrzeug besetzen — geprüft gegen Betriebs- und Rechtsregeln</p>
        </div>
        <button
          className={wochenansicht ? 'knopf-primaer' : 'knopf-schlicht'}
          onClick={() => setWochenansicht(!wochenansicht)}
        >
          Wochenübersicht
        </button>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      <div className="werkzeugleiste">
        <button className="knopf-schlicht" onClick={() => setDatum(verschiebe(datum, -1))}>
          ‹ Vortag
        </button>
        <input
          type="date"
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
          style={{ maxWidth: 170 }}
        />
        <button className="knopf-schlicht" onClick={() => setDatum(verschiebe(datum, 1))}>
          Folgetag ›
        </button>
        <button className="knopf-schlicht" onClick={() => setDatum(heuteAlsIso())}>
          Heute
        </button>
        {plan && (
          <span style={{ color: 'var(--grau-700)', fontWeight: 600, marginLeft: 6 }}>
            {WOCHENTAG[plan.wochentag] ?? plan.wochentag}
          </span>
        )}
      </div>

      {wochenansicht && <Wochenplan datum={datum} />}

      {laedt ? (
        <div className="laden">Wird geladen …</div>
      ) : !plan ? null : plan.kurseGesamt === 0 ? (
        <div className="karte">
          <div className="leer">
            An diesem Tag wird kein Kurs gefahren. Prüfen Sie den Wochentag oder die
            Gültigkeit der Kursblätter.
          </div>
        </div>
      ) : (
        <>
          <div className="karten-raster">
            <div className="kennzahl navy">
              <div className="wert">{plan.kurseGesamt}</div>
              <div className="beschriftung">Kurse an diesem Tag</div>
            </div>
            <div className={`kennzahl ${plan.vollBesetzt === plan.kurseGesamt ? '' : 'navy'}`}>
              <div className="wert">{plan.vollBesetzt}</div>
              <div className="beschriftung">vollständig besetzt</div>
            </div>
            <div className={`kennzahl ${plan.offen > 0 ? 'orange' : ''}`}>
              <div className="wert">{plan.offen + plan.teilweise}</div>
              <div className="beschriftung">
                offen{plan.teilweise > 0 && ` · davon ${plan.teilweise} teilweise`}
              </div>
            </div>
            <div className={`kennzahl ${plan.mitSperre > 0 ? 'rot' : plan.mitWarnung > 0 ? 'orange' : ''}`}>
              <div className="wert">{plan.mitSperre + plan.mitWarnung}</div>
              <div className="beschriftung">
                Befunde
                {plan.mitSperre > 0 && ` · ${plan.mitSperre} Sperre${plan.mitSperre > 1 ? 'n' : ''}`}
              </div>
            </div>
          </div>

          <div className="tabelle-huelle">
            <table>
              <thead>
                <tr>
                  <th>Kurs</th>
                  <th>Dienstzeit</th>
                  <th>Arbeitszeit</th>
                  <th>FzGr</th>
                  <th>Fahrer</th>
                  <th>Fahrzeug</th>
                  <th>Prüfung</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {plan.zeilen.map((z) => {
                  const stufe = schwerste(z)
                  return (
                    <tr
                      key={z.kursId}
                      style={
                        stufe === 'SPERRE'
                          ? { background: 'var(--rot-100)' }
                          : stufe === 'WARNUNG'
                            ? { background: 'var(--orange-100)' }
                            : undefined
                      }
                    >
                      <td>
                        <strong>{z.kursnummer}</strong>
                        {z.geteilterDienst && (
                          <span className="etikett orange" style={{ marginLeft: 6 }}>
                            geteilt
                          </span>
                        )}
                        {umleitungen[z.kursnummer] && (
                          <Link
                            to="/verkehrsmeldungen"
                            className="etikett navy"
                            style={{ marginLeft: 6, textDecoration: 'none' }}
                            title={umleitungen[z.kursnummer].join(' · ')}
                          >
                            ⚠ {umleitungen[z.kursnummer].length === 1
                              ? 'Umleitung'
                              : `${umleitungen[z.kursnummer].length} Meldungen`}
                          </Link>
                        )}
                      </td>
                      <td className="zahl">
                        {z.dienstbeginn !== null
                          ? `${alsUhrzeit(z.dienstbeginn)} – ${alsUhrzeit(z.dienstende)}`
                          : '–'}
                      </td>
                      <td className="zahl">{alsDauer(z.arbeitszeit)}</td>
                      <td className="zahl">{z.fahrzeuggruppe ?? '–'}</td>
                      <td>
                        {z.fahrerName ?? (
                          <span style={{ color: 'var(--grau-500)' }}>nicht besetzt</span>
                        )}
                      </td>
                      <td>
                        {z.fahrzeugKennzeichen ?? (
                          <span style={{ color: 'var(--grau-500)' }}>nicht besetzt</span>
                        )}
                      </td>
                      <td>
                        {z.befunde.length === 0 ? (
                          z.besetzt ? (
                            <span className="etikett gruen">in Ordnung</span>
                          ) : (
                            <span style={{ color: 'var(--grau-500)' }}>–</span>
                          )
                        ) : (
                          /* Zusammengefasst statt aufgelistet: Die Einzelmeldungen stehen
                             im Aufklapper darunter, damit die Zeilen gleich hoch bleiben. */
                          <button
                            className="knopf-schlicht"
                            style={{
                              padding: '4px 11px',
                              fontSize: '0.8rem',
                              borderColor:
                                stufe === 'SPERRE' ? 'var(--rot-500)' : 'var(--orange-500)',
                              color: stufe === 'SPERRE' ? 'var(--rot-500)' : 'var(--orange-600)',
                            }}
                            onClick={() =>
                              setOffen(offen === z.kursId ? null : z.kursId)
                            }
                          >
                            {z.befunde.filter((b) => b.schwere === 'SPERRE').length > 0 &&
                              `${z.befunde.filter((b) => b.schwere === 'SPERRE').length} Sperre `}
                            {z.befunde.filter((b) => b.schwere === 'WARNUNG').length > 0 &&
                              `${z.befunde.filter((b) => b.schwere === 'WARNUNG').length} Hinweis`}
                            {offen === z.kursId ? ' ▴' : ' ▾'}
                          </button>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {darfDisponieren && (
                          <>
                            <button
                              className="knopf-schlicht"
                              style={{ padding: '5px 12px' }}
                              onClick={() => setDialog(z)}
                            >
                              {z.zuteilungId ? 'Ändern' : 'Besetzen'}
                            </button>{' '}
                            {z.zuteilungId && (
                              <button
                                className="knopf-schlicht"
                                style={{ padding: '5px 10px', color: 'var(--rot-500)' }}
                                onClick={() => void loesen(z)}
                                title="Besetzung aufheben"
                              >
                                ×
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {offen !== null && (
            <div className="karte abschnitt" style={{ marginTop: 14 }}>
              <h2>
                Befunde zu Kurs{' '}
                {plan.zeilen.find((z) => z.kursId === offen)?.kursnummer}
              </h2>
              <ul style={{ margin: '10px 0 0', paddingLeft: 20, lineHeight: 1.8 }}>
                {plan.zeilen
                  .find((z) => z.kursId === offen)
                  ?.befunde.map((b, i) => (
                    <li key={i}>
                      <span
                        className={`etikett ${b.schwere === 'SPERRE' ? 'rot' : 'orange'}`}
                        style={{ marginRight: 8 }}
                      >
                        {b.schwere === 'SPERRE' ? 'Sperre' : 'Hinweis'}
                      </span>
                      {b.meldung}
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </>
      )}

      {dialog && (
        <Besetzungsdialog
          datum={datum}
          zeile={dialog}
          onFertig={() => {
            setDialog(null)
            void laden()
          }}
          onAbbruch={() => setDialog(null)}
        />
      )}
    </>
  )
}
